import { useEffect, useState } from 'react';
import { AnimatedNumber } from './AnimatedNumber';

interface ProgressRingProps {
  /** Percentuale reale (può essere <0 o >100: il disegno è clampato, il centro mostra il vero). */
  pct: number;
  size?: number;
  /** Spessore anello. Default 9. */
  stroke?: number;
  /** Tinta: esplicita o 'auto' (soglia → ink/rosso). */
  tone?: 'ink' | 'ok' | 'warn' | 'alert' | 'auto';
  /** Soglia per 'auto' (es. marginThreshold). Sotto → ambra/rosso. */
  threshold?: number;
  /** Etichetta piccola sotto la %. */
  label?: string;
  /** Mostra la % al centro. Default true. */
  showValue?: boolean;
  /** Suffisso del valore centrale. Default '%'. */
  suffix?: string;
}

/* Stroke piatto: ink per neutro e sopra soglia, rosso tenue/pieno sotto soglia */
const TONE_COLOR: Record<string, string> = {
  ink:   'var(--accent)',
  ok:    'var(--accent)',
  warn:  'var(--warn)',
  alert: 'var(--alert)',
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Anello di progresso in SVG puro. Si riempie all'ingresso (one-shot) e mostra
 * la % al centro col count-up. La chiusura del cerchio comunica "completo" in
 * pre-attentivo (Gestalt della chiusura), più veloce di leggere "73%".
 */
export function ProgressRing({
  pct, size = 88, stroke = 9, tone = 'ink', threshold, label, showValue = true, suffix = '%',
}: ProgressRingProps) {
  const [mounted, setMounted] = useState(false);
  const drawn = Math.max(0, Math.min(100, pct));

  // Tono effettivo: 'auto' diventa ink/rosso tenue/rosso rispetto alla soglia.
  const effTone = tone === 'auto'
    ? (threshold == null ? 'ink' : pct >= threshold ? 'ok' : pct >= threshold * 0.66 ? 'warn' : 'alert')
    : tone;
  const ringColor = TONE_COLOR[effTone] ?? TONE_COLOR.ink!;

  const r = (size - stroke) / 2;
  const cx = size / 2;
  const C = 2 * Math.PI * r;
  const target = C * (1 - drawn / 100);

  const reduce = prefersReducedMotion();
  useEffect(() => {
    if (reduce) { setMounted(true); return; }
    const t = requestAnimationFrame(() => requestAnimationFrame(() => setMounted(true)));
    return () => cancelAnimationFrame(t);
  }, [reduce]);

  const offset = mounted ? target : C;

  // Posizione della tacca soglia (se richiesta) sul cerchio.
  const tickAngle = threshold != null ? (Math.max(0, Math.min(100, threshold)) / 100) * 360 - 90 : null;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--line)" strokeWidth={stroke} />
        <circle
          cx={cx} cy={cx} r={r} fill="none"
          stroke={ringColor} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={offset}
          style={{ transition: reduce ? 'none' : 'stroke-dashoffset 900ms cubic-bezier(0.22,0.61,0.36,1)' }}
        />
        {tickAngle != null && (
          <line
            x1={cx + (r - stroke / 2 - 1) * Math.cos((tickAngle * Math.PI) / 180)}
            y1={cx + (r - stroke / 2 - 1) * Math.sin((tickAngle * Math.PI) / 180)}
            x2={cx + (r + stroke / 2 + 1) * Math.cos((tickAngle * Math.PI) / 180)}
            y2={cx + (r + stroke / 2 + 1) * Math.sin((tickAngle * Math.PI) / 180)}
            stroke="var(--text)" strokeWidth="1.5" strokeLinecap="round" opacity={0.5}
          />
        )}
      </svg>
      {showValue && (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
          <div>
            <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontVariantNumeric: 'tabular-nums', fontWeight: 800, fontSize: size * 0.26, letterSpacing: '-0.03em', lineHeight: 1 }}>
              <AnimatedNumber value={pct} format={(n) => `${Math.round(n)}${suffix}`} duration={900} />
            </div>
            {label && (
              <div style={{
                marginTop: 3,
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: Math.max(6.5, size * 0.082),
                letterSpacing: '0.02em',
                lineHeight: 1.05,
                color: 'var(--mute)',
                maxWidth: size - stroke * 2 - 8,
                marginInline: 'auto',
              }}>{label}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
